import './tracklist.component.scss';

import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { IconClock } from '../../../common/icons/icon-clock';
import { IconCloudArrowDown } from '../../../common/icons/icon-cloud-arrow-down';
import { IconExplicit } from '../../../common/icons/icon-explicit';
import { IconMagnifyingGlassPlus } from '../../../common/icons/icon-magnifying-glass-plus';
import { IconPlayerPause } from '../../../common/icons/icon-player-pause';
import { IconPlayerPlay } from '../../../common/icons/icon-player-play';
import { IconShuffle } from '../../../common/icons/icon-shuffle';
import { IconWaveform } from '../../../common/icons/icon-waveform/icon-waveform';
import { useTranslation } from '../../../i18n';
import { buttonize } from '../../../utils/a11y.utils';
import {
  getAllArtists,
  getArtworkUrl,
  getFeaturedArtists,
  getMainArtists,
  getTrackId,
  isTrackPlayable,
  stripRightsSymbol,
} from '../../models/album.helpers';
import type { Album, AudioFormat, Track } from '../../models/album.interface';
import { ArtLightbox } from '../art-lightbox/art-lightbox.component';
import { ReleaseCountdownComponent } from '../release-countdown/release-countdown.component';

interface TracklistProps {
  album: Album;
  currentTrackIndex: number;
  isPlaying: boolean;
  isShuffle: boolean;
  onPlayTrack: (index: number) => void;
  onTogglePlay: () => void;
  onPlayAll: () => void;
  onShuffle: () => void;
  onDownload?: (format: AudioFormat) => Promise<void>;
  onLoginToDownload?: () => void;
}

// Times of an hour or more count down as H:MM:SS (-2:29:28) instead of -149:28.
const formatTime = (s: number) => {
  const ss = Math.floor(s % 60)
    .toString()
    .padStart(2, '0');
  if (s < 3600) return `${Math.floor(s / 60)}:${ss}`;
  const m = Math.floor((s % 3600) / 60)
    .toString()
    .padStart(2, '0');
  return `${Math.floor(s / 3600)}:${m}:${ss}`;
};

const formatAlbumDuration = (duration: string): string => {
  const parts = duration.split(':').map(Number);
  let hours = 0,
    minutes = 0,
    seconds = 0;
  if (parts.length === 3) [hours, minutes, seconds] = parts;
  else if (parts.length === 2) [minutes, seconds] = parts;
  if (hours > 0) return `${hours} hr ${minutes} min`;
  return `${minutes} min ${seconds} sec`;
};

export const TracklistComponent = ({
  album,
  currentTrackIndex,
  isPlaying,
  isShuffle,
  onPlayTrack,
  onTogglePlay,
  onPlayAll,
  onShuffle,
  onDownload,
  onLoginToDownload,
}: TracklistProps) => {
  const { t } = useTranslation();
  const [showFormats, setShowFormats] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Each download request spends one of the code's downloads, so taps while one is in flight are
  // dropped. A ref rather than the state, so two taps before a re-render can't both get through.
  const downloadingRef = useRef(false);
  const downloadRef = useRef<HTMLDivElement>(null);
  const prevTrackIndexRef = useRef(currentTrackIndex);
  const sortedTracksRef = useRef<typeof sortedTracks>([]);

  useEffect(() => {
    if (!showFormats) return;
    const handleOutside = (e: MouseEvent) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target as Node)) {
        setShowFormats(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showFormats]);

  const isPresale = !!album.presaleDate && new Date(album.releaseDate) > new Date();

  const artists = getMainArtists(album.parties)
    .map((a) => a.name)
    .join(', ');
  // The backend withholds the release date entirely when the artist has asked that dates not be
  // shown (SNGL-7177), and `new Date(null)` is the epoch -- so an unguarded year renders as 1970.
  const releaseYear = album.releaseDate ? new Date(album.releaseDate).getFullYear() : null;
  const artworkUrl = getArtworkUrl(album.images);
  const sortedTracks = [...album.tracks].sort((a, b) => a.order - b.order);
  sortedTracksRef.current = sortedTracks;

  // Width of the widest duration on the list, in characters. Every row reserves it, so a row with no
  // duration (a masked track) still holds the column and the Exclusive badge beside it lines up with
  // the badges on rows that do. Same as the tracklist app block.
  const durationChars = Math.max(0, ...sortedTracks.map((track) => (track.duration ?? '').length));

  useEffect(() => {
    const prevIndex = prevTrackIndexRef.current;
    prevTrackIndexRef.current = currentTrackIndex;
    if (prevIndex === currentTrackIndex) return;
    const rows = document.querySelectorAll<HTMLElement>('.single-tracklist-track');
    const prevRow = rows[prevIndex];
    if (prevRow) {
      const span = prevRow.querySelector<HTMLElement>('.single-tracklist-track-duration');
      if (span) span.textContent = sortedTracksRef.current[prevIndex]?.duration ?? '';
    }
  }, [currentTrackIndex]);

  useEffect(() => {
    const handler = (e: Event) => {
      const { currentTime, duration } = (
        e as CustomEvent<{ currentTime: number; duration: number }>
      ).detail;
      const el = document.querySelector<HTMLElement>(
        '#active-track .single-tracklist-track-duration'
      );
      if (el && duration > 0)
        el.textContent = `-${formatTime(Math.max(0, duration - currentTime))}`;
    };
    window.addEventListener('audioTimeUpdate', handler);
    return () => window.removeEventListener('audioTimeUpdate', handler);
  }, []);

  return (
    <div class="single-tracklist">
      <div class="single-tracklist-header">
        {artworkUrl && (
          <div
            class="single-tracklist-artwork"
            {...buttonize(() => setLightboxOpen(true), `View artwork for ${album.title}`)}
          >
            <img src={artworkUrl} alt={album.title} />
            <span class="single-tracklist-artwork-zoom" aria-hidden="true">
              <IconMagnifyingGlassPlus width={15} height={15} fill="#fff" />
            </span>
          </div>
        )}
        {lightboxOpen && artworkUrl && (
          <ArtLightbox
            images={[{ url: artworkUrl.replace(/\/MEDIUM$/, '/FULL'), label: album.title }]}
            onClose={() => setLightboxOpen(false)}
          />
        )}
        <div class="single-tracklist-info">
          <h2 class="single-tracklist-title h2 single-h2">{album.title}</h2>
          <h5 class="single-tracklist-meta h5 single-h5">
            {artists}
            {releaseYear ? ` · ${releaseYear}` : ''} ·{' '}
            {t(album.tracks.length === 1 ? 'music.songCountOne' : 'music.songCount', {
              count: String(album.tracks.length),
            })}
          </h5>
          <div class="single-tracklist-controls">
            <button
              class="single-tracklist-play-btn btn button"
              onClick={onPlayAll}
              aria-label={isPlaying && currentTrackIndex >= 0 ? t('a11y.pause') : t('a11y.playAll')}
            >
              {isPlaying && currentTrackIndex >= 0 ? (
                <IconPlayerPause width={22} height={22} />
              ) : (
                <IconPlayerPlay width={22} height={22} />
              )}
            </button>
            <div
              class={`single-tracklist-shuffle ${isShuffle ? ' active' : ''}`}
              {...buttonize(onShuffle, 'Shuffle')}
              aria-pressed={isShuffle}
            >
              <IconShuffle width={30} height={30} />
            </div>
            {!isPresale &&
              (onLoginToDownload ? (
                <div class="single-download-wrap">
                  <button class="single-download-button" onClick={onLoginToDownload}>
                    {t('music.loginToDownload')}
                  </button>
                </div>
              ) : onDownload && album.audioFormats?.length > 0 ? (
                <div class="single-download-wrap" ref={downloadRef}>
                  <button class="single-download-button" onClick={() => setShowFormats((v) => !v)}>
                    <IconCloudArrowDown width={15} height={15} />
                    {t('music.download')}
                  </button>
                  {showFormats && (
                    <div class={`single-download-dropdown${downloading ? ' downloading' : ''}`}>
                      {album.audioFormats.map((fmt) => (
                        <div
                          key={fmt}
                          class="single-download-format"
                          aria-disabled={downloading}
                          {...buttonize(async () => {
                            if (downloadingRef.current) return;
                            downloadingRef.current = true;
                            setDownloading(true);
                            try {
                              await onDownload(fmt);
                              setShowFormats(false);
                            } finally {
                              downloadingRef.current = false;
                              setDownloading(false);
                            }
                          })}
                        >
                          {fmt.format}
                          <span class="single-download-size">{fmt.size.toFixed(2)} MB</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null)}
          </div>
        </div>
      </div>

      {album.releaseDate && (
        <ReleaseCountdownComponent key={album.releaseDate} releaseDate={album.releaseDate} />
      )}

      <div class="single-tracklist-tracks" style={{ '--tlDurationWidth': `${durationChars}ch` }}>
        <div class="single-tracklist-tracks-header">
          <span>#</span>
          <span>{t('music.trackTitle')}</span>
          <span class="single-tracklist-header-clock">
            <IconClock width={14} height={14} />
          </span>
        </div>

        {sortedTracks.map((track: Track, i: number) => {
          const playable = isTrackPlayable(track);
          const pending = track.released === false || track.placeholder === true;
          const isActive = playable && i === currentTrackIndex;
          const featuredArtists = getFeaturedArtists(track.parties).map((a) => a.name);
          const trackTitle = featuredArtists.length
            ? `${track.title} (feat. ${featuredArtists.join(', ')})`
            : track.title;
          const trackArtists = getMainArtists(track.parties)
            .map((a) => a.name)
            .join(', ');
          return (
            <div
              key={getTrackId(track) ?? i}
              id={isActive ? 'active-track' : undefined}
              class={`single-tracklist-track${isActive ? ' active' : ''}${playable ? '' : ' static'}${pending ? ' pending' : ''}`}
              {...(playable
                ? buttonize(() => onPlayTrack(i), `${trackTitle} — ${trackArtists}`)
                : {})}
              aria-current={isActive}
            >
              <span class="single-tracklist-track-num">
                <span class="single-tracklist-track-num-text">{track.order}</span>
                {playable && (
                  <span
                    class="single-tracklist-track-num-icon"
                    {...(isActive
                      ? buttonize(
                          (e) => {
                            e.stopPropagation();
                            onTogglePlay();
                          },
                          isPlaying ? 'Pause' : 'Play'
                        )
                      : {})}
                  >
                    {isActive && isPlaying ? (
                      <>
                        <IconWaveform width={20} height={20} animated />
                        <IconPlayerPause width={14} height={14} />
                      </>
                    ) : (
                      <IconPlayerPlay width={14} height={14} />
                    )}
                  </span>
                )}
              </span>
              <div class="single-tracklist-track-info">
                <p class="single-tracklist-track-title">
                  {isActive && (
                    <span class="single-tracklist-mobile-waveform">
                      <IconWaveform width={16} height={16} animated={isPlaying} />
                    </span>
                  )}
                  <span class="single-tracklist-title-text">{trackTitle}</span>
                </p>
                <div class="single-tracklist-track-artist">
                  {track.explicit && <IconExplicit width={12} height={12} />}
                  <span>{trackArtists}</span>
                </div>
              </div>
              <div class="single-tracklist-track-end">
                {track.exclusive && (
                  <span class="single-tracklist-exclusive">{t('music.exclusive')}</span>
                )}
                <span class="single-tracklist-track-duration">{track.duration}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div class="single-tracklist-footer">
        <div class="single-tracklist-footer-meta">
          <h6 class="single-tracklist-footer-date h6 single-h6">
            {album.releaseDate
              ? new Date(album.releaseDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : ''}
          </h6>
          <h6 class="single-tracklist-footer-count h6 single-h6">
            {t(album.tracks.length === 1 ? 'music.songCountOne' : 'music.songCount', {
              count: String(album.tracks.length),
            })}
            {album.duration ? ` • ${formatAlbumDuration(album.duration)}` : ''}
          </h6>
        </div>
        {album.publishingLine && (
          <small>
            <span style="font-family: Arial, sans-serif;">&#8471;</span>{' '}
            {stripRightsSymbol(album.publishingLine)}
          </small>
        )}
        {album.copyrightLine && (
          <small>
            <span style="font-family: Arial, sans-serif;">&#169;</span>{' '}
            {stripRightsSymbol(album.copyrightLine)}
            {album.copyrightRegistrationNumber && ` (${album.copyrightRegistrationNumber})`}
          </small>
        )}
      </div>
    </div>
  );
};
